import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { MultipleAutocompleteAdapter } from 'components/enhanced/form/adapters/selects/MultipleAutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { IsSuperAdmin } from 'shared/permissions/IsSuperAdmin';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import { ITenantSettings } from './types';
import { movementReasonService } from 'features/inventory/movements/api/movement-reason.service';
import { SectionHeader } from 'components/ui/typography';

interface TenantSettingsFormParams {
  tenantSettings: ITenantSettings;
  onSubmit: any;
}

export const TenantSettingsForm = ({ tenantSettings, onSubmit }: TenantSettingsFormParams) => {
  const { t } = useTranslation();

  const [movementReasons, setMovementReasons] = useState<Option[]>([]);

  useEffect(() => {
    movementReasonService.getAllPaginated().then((res) => {
      setMovementReasons(res.results);
    });
  }, []);

  return (
    <div>
      {tenantSettings && (
        <EnhancedForm onSubmit={onSubmit} autoStyleLayout columns={6}>
          <IsSuperAdmin>
            <IdentityField
              gridSpan={6}
              name="error_reports_email"
              label={t(`entities.tenant-settings.error_reports_email`)}
              initialValue={tenantSettings.error_reports_email}
              component={TextFieldAdapter}
              type="email"
            />

            {/* TOKEN METABASE */}
            <SectionHeader title={t('entities.tenant-settings.metabase-info')} gridSpan />
            <IdentityField
              gridSpan={6}
              name="metabase_token"
              label={t(`entities.tenant-settings.metabase_token`)}
              initialValue={tenantSettings.metabase_token}
              component={TextFieldAdapter}
              type="text"
            />
          </IsSuperAdmin>
          {/* INFORMAZIONI DI BASE */}
          <SectionHeader title={t('entities.tenant-settings.base-info')} gridSpan />
          <IdentityField
            gridSpan={2}
            name="language_code"
            label={t(`entities.tenant-settings.language_code`)}
            initialValue={tenantSettings.language_code}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.language_code')}
            type="text"
          />
          <IdentityField
            gridSpan={2}
            name="default_unload_reason"
            component={AutocompleteAdapter}
            options={movementReasons}
            initValue={tenantSettings.default_unload_reason}
            label={t('entities.tenant-settings.default_unload_reason')}
            type="text"
          />
          <IdentityField
            gridSpan={2}
            name="days_before_expiration_alert"
            label={t(`entities.tenant-settings.days_before_expiration_alert`)}
            initialValue={tenantSettings.days_before_expiration_alert}
            component={TextFieldAdapter}
            type="number"
          />
          {/* ANALISI DEI COSTI */}
          <SectionHeader title={t('entities.tenant-settings.consumption-analysis')} gridSpan />
          <IdentityField
            gridSpan={6}
            name="cost_analysis_reasons"
            component={MultipleAutocompleteAdapter}
            options={movementReasons}
            initValue={tenantSettings.cost_analysis_reasons}
            label={t('entities.tenant-settings.cost-analysis-reasons')}
            type="text"
          />
          {/* CONFIGURAZIONE VISUALIZZAZIONE MENU */}
          <SectionHeader title={t('entities.tenant-settings.metabase_dashboard_tabs')} gridSpan />
          <IdentityField
            gridSpan={6}
            name="metabase_dashboard_tabs"
            initialValue={tenantSettings.metabase_dashboard_tabs}
            component={TextFieldAdapter}
            label={t('entities.tenant-settings.metabase_dashboard_tabs_reasons')}
            type="text"
          />
          {/* 
          ORDINI DI ACQUISTO 
          */}
          <SectionHeader title={t('entities.tenant-settings.order-info')} gridSpan />
          <IdentityField
            gridSpan={6}
            name="split_orders_by_supplier"
            label={t('entities.tenant-settings.split_orders_by_supplier')}
            initialValue={tenantSettings.split_orders_by_supplier}
            component={CheckboxAdapter}
            placeholder={t('entities.tenant-settings.split_orders_by_supplier')}
            type="checkbox"
          />
          <IdentityField
            gridSpan={2}
            name="company_name"
            label={t(`entities.tenant-settings.company_name`)}
            initialValue={tenantSettings.company_name}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.company_name')}
            type="text"
          />
          <IdentityField
            name="city"
            label={t(`entities.tenant-settings.city`)}
            initialValue={tenantSettings.city}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.city')}
            type="text"
          />
          <IdentityField
            name="district"
            label={t(`entities.tenant-settings.district`)}
            initialValue={tenantSettings.district}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.district')}
            type="text"
          />
          <IdentityField
            name="zip_code"
            label={t(`entities.tenant-settings.zip_code`)}
            initialValue={tenantSettings.zip_code}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.zip_code')}
            type="text"
          />
          <IdentityField
            name="country"
            label={t(`entities.tenant-settings.country`)}
            initialValue={tenantSettings.country}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.country')}
            type="text"
          />
          <IdentityField
            gridSpan={3}
            name="address"
            label={t(`entities.tenant-settings.address`)}
            initialValue={tenantSettings.address}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.address')}
            type="text"
          />
          <IdentityField
            gridSpan={3}
            name="address_2"
            label={t(`entities.tenant-settings.address_2`)}
            initialValue={tenantSettings.address_2}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.address_2')}
            type="text"
          />
          {/* Ordini di acquisto (ditta 2) */}
          <SectionHeader title={t('entities.tenant-settings.order-info-2')} gridSpan />
          <IdentityField
            gridSpan={2}
            name="company_name_extra"
            label={t(`entities.tenant-settings.company_name`)}
            initialValue={tenantSettings.company_name_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.company_name')}
            type="text"
          />
          <IdentityField
            name="city_extra"
            label={t(`entities.tenant-settings.city`)}
            initialValue={tenantSettings.city_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.city')}
            type="text"
          />
          <IdentityField
            name="district_extra"
            label={t(`entities.tenant-settings.district`)}
            initialValue={tenantSettings.district_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.district')}
            type="text"
          />
          <IdentityField
            name="zip_code_extra"
            label={t(`entities.tenant-settings.zip_code`)}
            initialValue={tenantSettings.zip_code_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.zip_code')}
            type="text"
          />
          <IdentityField
            name="country_extra"
            label={t(`entities.tenant-settings.country`)}
            initialValue={tenantSettings.country_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.country')}
            type="text"
          />
          <IdentityField
            gridSpan={3}
            name="address_extra"
            label={t(`entities.tenant-settings.address`)}
            initialValue={tenantSettings.address_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.address')}
            type="text"
          />
          <IdentityField
            gridSpan={3}
            name="address_2_extra"
            label={t(`entities.tenant-settings.address_2`)}
            initialValue={tenantSettings.address_2_extra}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.address_2')}
            type="text"
          />

          {/*Dati fatturazione*/}
          <SectionHeader title={t('entities.tenant-settings.invoice-info')} gridSpan />

          <IdentityField
            name="vat_number"
            label={t(`entities.tenant-settings.vat_number`)}
            initialValue={tenantSettings.vat_number}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.vat_number')}
            type="text"
          />

          <IdentityField
            name="cig"
            label={t(`entities.tenant-settings.cig`)}
            initialValue={tenantSettings.cig}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.cig')}
            type="text"
          />

          <IdentityField
            name="cuu"
            label={t(`entities.tenant-settings.cuu`)}
            initialValue={tenantSettings.cuu}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.cuu')}
            type="text"
          />

          <IdentityField
            name="email"
            label={t(`entities.tenant-settings.email`)}
            initialValue={tenantSettings.email}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.email')}
            type="text"
          />

          <IdentityField
            name="invoice_telephone"
            label={t(`entities.tenant-settings.invoice_telephone`)}
            initialValue={tenantSettings.invoice_telephone}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.invoice_telephone')}
            type="text"
          />

          <IdentityField
            name="invoice_email"
            label={t(`entities.tenant-settings.invoice_email`)}
            initialValue={tenantSettings.invoice_email}
            component={TextFieldAdapter}
            placeholder={t('entities.tenant-settings.invoice_email')}
            type="text"
          />

          {/*Comunicazioni*/}
          <SectionHeader title={t('entities.tenant-settings.communications')} gridSpan />

          <IdentityField
            gridSpan={6}
            name="orders_default_email_body"
            initialValue={tenantSettings.orders_default_email_body}
            component={TextFieldAdapter}
            label={t('global.default_orders_message')}
            placeholder={t('global.default_orders_message')}
            type="text"
            multiline
            rows={8}
          />

          <IdentityField
            gridSpan={6}
            name="ticketing_default_email_body"
            initialValue={tenantSettings.ticketing_default_email_body}
            component={TextFieldAdapter}
            label={t('global.default_ticketing_message')}
            placeholder={t('global.message-placeholder')}
            type="text"
            multiline
            rows={8}
          />

          <IdentityField
            gridSpan={2}
            name="cc_orders"
            label={t(`entities.tenant-settings.cc_orders`)}
            placeholder="mario.verdi@mail.it; francesco.bianchi@mail.it; franco.rossi@mail.it"
            initialValue={tenantSettings.cc_orders}
            component={TextFieldAdapter}
            type="text"
          />

          <IdentityField
            gridSpan={2}
            name="cc_credit_memos"
            label={t(`entities.tenant-settings.cc_credit_memos`)}
            placeholder="mario.verdi@mail.it; francesco.bianchi@mail.it; franco.rossi@mail.it"
            initialValue={tenantSettings.cc_credit_memos}
            component={TextFieldAdapter}
            type="text"
          />

          <IdentityField
            gridSpan={2}
            name="cc_ticketing"
            label={t(`entities.tenant-settings.cc_ticketing`)}
            placeholder="mario.verdi@mail.it; francesco.bianchi@mail.it; franco.rossi@mail.it"
            initialValue={tenantSettings.cc_ticketing}
            component={TextFieldAdapter}
            type="text"
          />
        </EnhancedForm>
      )}
    </div>
  );
};
